import React from 'react';
import { User, Phone, Mail, MapPin, Building2, ShieldCheck, Edit, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { currentUser } from '../mock/mockData';
import { toast } from '../hooks/use-toast';

export default function Profile() {
  const save = (e) => {
    e.preventDefault();
    toast({ title: 'Profile updated!', description: 'Your changes have been saved.' });
  };

  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <h1 className="text-2xl lg:text-3xl font-extrabold text-[#0B2545] font-[Poppins]">Profile</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your account and business details.</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#00A651]/5 rounded-full blur-2xl"></div>
        <div className="flex flex-wrap items-center gap-5">
          <div className="w-20 h-20 rounded-2xl brand-gradient flex items-center justify-center text-white font-bold text-2xl">{currentUser.avatar}</div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-[#0B2545]">{currentUser.name}</h2>
              {currentUser.verified && <Badge className="bg-[#00A651]/10 text-[#00A651] border-0"><ShieldCheck className="w-3 h-3 mr-1" />Verified</Badge>}
            </div>
            <p className="text-sm text-gray-600 mt-1">{currentUser.role} • {currentUser.company}</p>
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{currentUser.phone}</span>
              <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{currentUser.email}</span>
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{currentUser.city}</span>
            </div>
          </div>
          <Button variant="outline"><Edit className="w-4 h-4 mr-2" />Edit</Button>
        </div>
      </div>

      <form onSubmit={save} className="bg-white rounded-2xl border border-gray-100 p-6 space-y-5">
        <h2 className="text-lg font-bold text-[#0B2545]">Business Details</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">Full Name</Label>
            <Input defaultValue={currentUser.name} className="h-11 mt-1.5" />
          </div>
          <div>
            <Label className="text-sm">Mobile Number</Label>
            <Input defaultValue={currentUser.phone} className="h-11 mt-1.5" />
          </div>
          <div>
            <Label className="text-sm">Email</Label>
            <Input defaultValue={currentUser.email} className="h-11 mt-1.5" />
          </div>
          <div>
            <Label className="text-sm">Company / Fleet Name</Label>
            <Input defaultValue={currentUser.company} className="h-11 mt-1.5" />
          </div>
          <div>
            <Label className="text-sm">GSTIN</Label>
            <Input defaultValue={currentUser.gstin} className="h-11 mt-1.5 font-mono" />
          </div>
          <div>
            <Label className="text-sm">City & State</Label>
            <Input defaultValue={`${currentUser.city}, ${currentUser.state}`} className="h-11 mt-1.5" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button type="submit" className="bg-[#00A651] hover:bg-[#008a43] text-white">Save Changes</Button>
          <Button type="button" variant="outline">Cancel</Button>
        </div>
      </form>

      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h2 className="text-lg font-bold text-[#0B2545] mb-4">Verification Status</h2>
        <div className="space-y-3">
          {[
            { label: 'Mobile Verified', ok: true },
            { label: 'Email Verified', ok: true },
            { label: 'GSTIN Verified', ok: true },
            { label: 'PAN Verified', ok: true },
            { label: 'Bank Account Verified', ok: true },
            { label: 'Aadhaar Verified', ok: false },
          ].map((v, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
              <span className="text-sm text-[#0B2545]">{v.label}</span>
              {v.ok ? <Badge className="bg-[#00A651]/10 text-[#00A651] border-0"><ShieldCheck className="w-3 h-3 mr-1" />Verified</Badge> : <Button size="sm" variant="outline">Verify Now</Button>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
